import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom';
import pkg from '../../package.json';

const dsn = import.meta.env.VITE_SENTRY_DSN;

export function initSentry() {
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: `heymakr@${pkg.version}`,
    debug: import.meta.env.DEV,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration(),
      Sentry.browserProfilingIntegration(),
      Sentry.httpClientIntegration({
        failedRequestStatusCodes: [[400, 599]],
      }),
      Sentry.captureConsoleIntegration({ levels: ['warn', 'error'] }),
      Sentry.extraErrorDataIntegration({ depth: 5 }),
      Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] }),
    ],

    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    profileSessionSampleRate: 1.0,

    enableLogs: true,

    tracePropagationTargets: ['localhost', /^\//],
    sendDefaultPii: false,
  });
}

export function setAuthUser(
  userId: string,
  email: string,
  orgId: string | null,
  role: string | null,
) {
  Sentry.setUser({ id: userId, email });
  if (orgId) Sentry.setTag('org_id', orgId);
  if (role) Sentry.setTag('user_role', role);
}

export function clearAuthUser() {
  Sentry.setUser(null);
}

export function captureEdgeFunctionError(
  functionName: string,
  status: number,
  body: string,
) {
  Sentry.captureException(new Error(`API function "${functionName}" failed (${status})`), {
    tags: { function: functionName },
    extra: { status, responseBody: body },
  });
  Sentry.metrics.count('api_function.error', 1, {
    attributes: { function: functionName, status: String(status) },
  });
}

export function addActionBreadcrumb(action: string, data?: Record<string, unknown>) {
  Sentry.addBreadcrumb({
    category: 'user-action',
    message: action,
    data,
    level: 'info',
  });
}

export async function traceEdgeFn<T>(
  functionName: string,
  fn: () => Promise<T>,
): Promise<T> {
  return Sentry.startSpan(
    { op: 'http.client', name: `api-fn:${functionName}` },
    async () => {
      const start = performance.now();
      try {
        const result = await fn();
        Sentry.metrics.distribution('api_function.duration', performance.now() - start, {
          unit: 'millisecond',
          attributes: { function: functionName },
        });
        Sentry.metrics.count('api_function.call', 1, {
          attributes: { function: functionName },
        });
        return result;
      } catch (err) {
        Sentry.metrics.distribution('api_function.duration', performance.now() - start, {
          unit: 'millisecond',
          attributes: { function: functionName },
        });
        throw err;
      }
    },
  );
}

export { Sentry };
